/**
 * receipt-html.js — Única fuente de verdad para el diseño del recibo.
 *
 * Genera el HTML del recibo como string. Usado por:
 * - receipt-detail.js (imprimir, descargar PDF, compartir)
 * - El backend (templates/receipt-template.js) tiene su propia copia
 *   porque no puede importar desde el dashboard — pero el diseño
 *   debe mantenerse sincronizado con este archivo.
 *
 * Para personalizar el diseño, editar SOLO este archivo.
 */

import logoUrl from '../assets/images/dashboard/main-logo.png';

export const generateReceiptHTML = (receipt) => {
    if (!receipt) return '';

    const sym = getCurrencySymbol(receipt.currency);
    const formattedDate = new Date(receipt.createdAt).toLocaleDateString('es-VE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });

    const itemsRows = (receipt.items || [])
        .map(
            (item, i) => `
        <tr>
            <td style="padding: 10px 12px; border-bottom: 1px solid #eee;">${i + 1}</td>
            <td style="padding: 10px 12px; border-bottom: 1px solid #eee;">${item.name}</td>
            <td style="padding: 10px 12px; border-bottom: 1px solid #eee; text-align: center;">${item.quantity}</td>
            <td style="padding: 10px 12px; border-bottom: 1px solid #eee; text-align: right;">${sym} ${formatAmount(item.unitPrice, receipt.currency)}</td>
            <td style="padding: 10px 12px; border-bottom: 1px solid #eee; text-align: right;">${sym} ${formatAmount(item.subtotal, receipt.currency)}</td>
        </tr>`,
        )
        .join('');

    const holderIdBlock = receipt.holderId
        ? `<div class="info-block">
                <div class="label">${receipt.holderId.length > 10 ? 'RIF' : 'Cédula'}</div>
                <div class="value">${receipt.holderId}</div>
            </div>`
        : '';

    const holderContactBlock =
        receipt.holderPhone || receipt.holderAddress
            ? `<div class="info-row">
                ${receipt.holderPhone ? `<div class="info-block"><div class="label">Teléfono</div><div class="value">${receipt.holderPhone}</div></div>` : ''}
                ${receipt.holderAddress ? `<div class="info-block"><div class="label">Dirección</div><div class="value">${receipt.holderAddress}</div></div>` : ''}
            </div>`
            : '';

    return `
<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Recibo #${receipt.receiptNumber} — Aguaman</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
            background-color: #fff;
            padding: 20px;
            color: #333;
        }
        .receipt-container { max-width: 800px; margin: 0 auto; display: flex; flex-direction: column; min-height: 1056px; }
        .receipt-header {
            background: linear-gradient(135deg, #00a4d3, #007ba8);
            color: #fff; padding: 20px 40px;
            display: flex; justify-content: space-between; align-items: center;
            border-radius: 8px 8px 0 0;
        }
        .receipt-header .membrete {
            width: 50%;
        }
        .receipt-header .membrete img {
            width: 100%; height: auto; border-radius: 6px;
        }
        .receipt-header .receipt-meta { text-align: right; }
        .receipt-header .receipt-meta .label { font-size: 12px; opacity: 0.8; text-transform: uppercase; }
        .receipt-header .receipt-meta .number { font-size: 22px; font-weight: 700; }
        .receipt-body { padding: 30px 40px; border: 1px solid #eee; border-top: none; flex: 1; }
        .info-row { display: flex; margin-bottom: 15px; gap: 40px; }
        .info-block { flex: 1; }
        .info-block .label { font-size: 11px; color: #888; text-transform: uppercase; margin-bottom: 4px; }
        .info-block .value { font-size: 16px; font-weight: 600; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th { background: #f8f9fa; padding: 12px; text-align: left; font-size: 13px; color: #666; text-transform: uppercase; border-bottom: 2px solid #00a4d3; }
        td { font-size: 14px; }
        .total-section { margin-top: 20px; display: flex; justify-content: flex-end; }
        .total-box { background: #f8f9fa; padding: 15px 25px; border-radius: 6px; border-left: 4px solid #00a4d3; }
        .total-box .total-amount { font-size: 24px; font-weight: 700; color: #00a4d3; }
        .total-box .total-label { font-size: 12px; color: #888; text-transform: uppercase; }
        .observation { margin-top: 20px; padding: 12px 15px; background: #fffde8; border-radius: 4px; border-left: 3px solid #ffc107; font-size: 14px; color: #666; }
        .receipt-footer { padding: 20px 40px; background: #f8f9fa; text-align: center; font-size: 12px; color: #999; border: 1px solid #eee; border-top: none; border-radius: 0 0 8px 8px; margin-top: auto; }
        @page { size: letter; margin: 10mm; }
        @media print { body { padding: 0; } .receipt-container { min-height: 100vh; max-width: 100%; } }
    </style>
</head>
<body>
    <div class="receipt-container">
        <div class="receipt-header">
            <div class="membrete">
                <img src="${logoUrl}" alt="Aguaman">
            </div>
            <div class="receipt-meta">
                <div class="label">Recibo N°</div>
                <div class="number">#${receipt.receiptNumber}</div>
            </div>
        </div>
        <div class="receipt-body">
            <div class="info-row">
                <div class="info-block">
                    <div class="label">Titular</div>
                    <div class="value">${receipt.holderName}</div>
                </div>
                ${holderIdBlock}
                <div class="info-block">
                    <div class="label">Fecha</div>
                    <div class="value">${formattedDate}</div>
                </div>
            </div>
            ${holderContactBlock}
            <table>
                <thead>
                    <tr>
                        <th>#</th><th>Producto</th>
                        <th style="text-align:center;">Cant.</th>
                        <th style="text-align:right;">Precio</th>
                        <th style="text-align:right;">Subtotal</th>
                    </tr>
                </thead>
                <tbody>${itemsRows}</tbody>
            </table>
            ${receipt.observation ? `<div class="observation"><strong>Observación:</strong> ${receipt.observation}</div>` : ''}
            <div class="total-section">
                <div class="total-box">
                    <div class="total-label">Total</div>
                    <div class="total-amount">${sym} ${formatAmount(receipt.totalAmount, receipt.currency)}</div>
                    ${receipt.paymentMethod ? `<div style="margin-top:8px;font-size:13px;color:#666;">Pago: ${receipt.paymentMethod}</div>` : ''}
                </div>
            </div>
        </div>
        <div class="receipt-footer">
            <strong>Aguaman Maldonado Romer F.P.</strong><br>
            RIF: V-20608546-7<br>
            0414-7353821<br>
            aguamandelivery.com
        </div>
    </div>
</body>
</html>`;
};

export const getCurrencySymbol = (currency) => {
    const symbols = { USD: '$', VES: 'Bs.', COP: '$' };
    return symbols[currency] || '$';
};

/**
 * Formatea un número según la moneda:
 * - VES y COP: formato venezolano/colombiano — miles con punto, decimales con coma
 * - USD: formato internacional — miles con coma, decimales con punto
 */
export const formatAmount = (amount, currency) => {
    const num = Number(amount) || 0;
    if (currency === 'VES' || currency === 'COP') {
        // Formato es-VE: 1.234,56
        return num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    // USD y otros: 1,234.56
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};